import { ComponentProps } from "PFApp/components/activity_form/field_factory/component_prop_types";
import { CustomValuesEditField } from "PFApp/components/custom_values_edit_field";
import { FIELD_CUSTOM_FIELDS_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { useConnectedFields } from "PFApp/hooks/activity_form/use_connected_fields";
import { MultiActivityForm, RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { allowsNewValues } from "PFCore/helpers/custom_type";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { ActivityPureCustomValue, FeatureFlag, Subtemplate } from "PFCore/types";
import { useWatch } from "react-hook-form";

import { useFormActivityCreator } from "../../../../hooks/use_form_activity_creator";
import { MatchAttributeCell } from "./match_attribute_cell";

export type CustomFieldCellProps = ComponentProps & {
  value: ActivityPureCustomValue[];
  subtemplate: Subtemplate | null;
  title: string;
  roleFormKey: RoleFormKey;
  disabled?: boolean;
  error: string;
  className?: string;
};

export const CustomFieldCell = ({
  property,
  subtemplate,
  value,
  variant,
  portalRef,
  title,
  formattedTitle,
  roleFormKey,
  dropdownWidth,
  disabled,
  error,
  className,
  onChange,
  tabIndex,
  style,
  inputRef
}: CustomFieldCellProps) => {
  const isEnabled = useIsFeatureEnabled();
  const { customTypes } = useCustomTypes();
  const roleTemplate = useTemplate(TemplateKey.Role)!;
  const customFields = useWatch<MultiActivityForm, `${RoleFormKey}.${typeof FIELD_CUSTOM_FIELDS_KEY}`>({
    name: `${roleFormKey}.${FIELD_CUSTOM_FIELDS_KEY}`
  });
  const activityCreator = useFormActivityCreator(roleFormKey);
  const connectedValuesIds = useConnectedFields({
    property,
    template: roleTemplate,
    subtemplate,
    customFields
  });

  const activityCanCreateValues = isEnabled(FeatureFlag.ActivityCanCreateValues);
  const customType = customTypes.find((type) => type.name === property.name)!;
  const isMultiple = property.kind === "multiple";

  if (property.match) {
    return (
      <MatchAttributeCell
        customType={customType}
        isMultiple={isMultiple}
        connectedValuesIds={connectedValuesIds}
        roleFormKey={roleFormKey}
        value={value || []}
        title={title}
        formattedTitle={formattedTitle}
        onChange={onChange}
        disabled={disabled}
        error={error}
        activityCreator={activityCreator}
        tabIndex={tabIndex}
        style={style}
        buttonRef={inputRef}
      />
    );
  }

  return (
    <CustomValuesEditField
      variant={variant}
      customType={customType}
      connectedValuesIds={connectedValuesIds}
      values={value}
      multi={isMultiple}
      errors={error}
      adminPage={false}
      label=""
      title={formattedTitle}
      tip=""
      letClear
      letCreate={activityCanCreateValues && allowsNewValues(customType)}
      handleChange={onChange}
      hasValuesWithExpiryDate={false}
      isActivity
      activityCreator={activityCreator}
      portalRef={portalRef}
      dropdownWidth={dropdownWidth}
      disabled={disabled}
      classes={{ calendarInput: className }}
      focusSearchInputOnOpen={false}
      tabIndex={tabIndex}
      style={style}
      inputRef={inputRef}
    />
  );
};
